import React, { useState, useMemo } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import dayjs from "dayjs";
import { ReactComponent as ValidIcon } from "../../../../Assets/Icons/validsvg.svg";
import { ReactComponent as RulesIcon } from "../../../../Assets/Icons/rules&regulationsvg.svg";
import { ReactComponent as InStoreIcon } from "../../../../Assets/Icons/instoresvg.svg";
import { ReactComponent as WebIcon } from "../../../../Assets/Icons/webIcon.svg";
import { ReactComponent as CategoryIcon } from "../../../../Assets/Icons/categoryIcon.svg";
import { ReactComponent as FoodIcon } from "../../../../Assets/Icons/FoodAndBeverage.svg";

import { getS3PublicUrl } from "../../../../utils/s3";

const OfferDetails = ({ offerData }) => {
  const [showAllRules, setShowAllRules] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Combine thumbnail and preview images for the carousel
  const allImages = useMemo(() => {
    const images = [];
    if (offerData?.thumbnailImage) images.push(offerData.thumbnailImage);
    if (Array.isArray(offerData?.previewImages)) {
      offerData.previewImages.forEach(img => {
        if (img && img !== offerData.thumbnailImage) images.push(img);
      });
    }
    return images;
  }, [offerData?.thumbnailImage, offerData?.previewImages]);

  var settings = {
    dots: allImages.length > 1,
    arrows: false,
    infinite: true,
    autoplay: true,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    cssEase: "cubic-bezier(0.16, 1, 0.3, 1)",
    autoplaySpeed: 4000,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
  };

  // Compute consistent discount label and type tag
  const discountText = (() => {
    if (offerData?.discountTitle) return offerData.discountTitle;
    switch (offerData?.offerDiscountType) {
      case "FREE_OFFER":
        return "Free Offer";
      case "DISCOUNTED_PERCENT":
        return `Discounted Offer ${offerData?.discountRange || 0}%`;
      case "FIXED_DOLLAR":
        return `$${offerData?.fixedDollarAmount || 0} Fixed Price`;
      case "BUY_ONE_GET_ONE_FREE":
        return "Buy 1 Get 1 Free";
      case "BUNDLE_OFFER":
        return "Bundle Offer";
      default:
        return offerData?.discountRange !== undefined && offerData?.discountRange !== null
          ? `Discounted Offer ${offerData?.discountRange}%`
          : null;
    }
  })();

  const offerTypeTag = offerData?.offerType === 'INSTORE_SWOODLE_OFFER'
    ? 'In Store Offer'
    : (offerData?.offerType === 'ONLINE_OFFER' ? 'Online Offer' : '');

  // 1. High-Quality Inline Icons to fix blurriness
  const NewDiscountIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 15L15 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 10C9.55228 10 10 9.55228 10 9C10 8.44772 9.55228 8 9 8C8.44772 8 8 8.44772 8 9C8 9.55228 8.44772 10 9 10Z" fill="white" />
      <path d="M15 16C15.5523 16 16 15.5523 16 15C16 14.4477 15.5523 14 15 14C14.4477 14 14 14.4477 14 15C14 15.5523 14.4477 16 15 16Z" fill="white" />
    </svg>
  );

  const NewInStoreIcon = () => (
    <svg width="14" height="14" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.74981 10.5V6.75C6.74981 6.65054 6.78932 6.55516 6.85965 6.48484C6.92997 6.41451 7.02536 6.375 7.12481 6.375H8.62481C8.72427 6.375 8.81965 6.41451 8.88998 6.48484C8.9603 6.55516 8.99981 6.65054 8.99981 6.75V10.5M6.74981 10.5H1.17981M6.74981 10.5H8.99981M8.99981 10.5H10.8198M10.1248 10.5V4.6745M10.1248 4.6745C9.82486 4.84755 9.47428 4.91152 9.13253 4.85557C8.79079 4.79961 8.47892 4.62717 8.24981 4.3675C7.97481 4.6785 7.57281 4.875 7.12481 4.875C6.91195 4.87521 6.70149 4.83 6.50749 4.7424C6.3135 4.65479 6.14042 4.52681 5.99981 4.367C5.72481 4.6785 5.32281 4.875 4.87481 4.875C4.66195 4.87521 4.45149 4.83 4.25749 4.7424C4.0635 4.65479 3.89042 4.52681 3.74981 4.367C3.52077 4.62676 3.20893 4.79929 2.86718 4.85534C2.52544 4.91138 2.17482 4.84749 1.87481 4.6745M10.1248 4.6745C10.3241 4.55945 10.4943 4.4001 10.6221 4.20876C10.75 4.01742 10.8321 3.79923 10.8621 3.57108C10.8921 3.34292 10.8692 3.11092 10.7952 2.89302C10.7212 2.67513 10.5981 2.47718 10.4353 2.3145L9.84031 1.72C9.69979 1.57931 9.50916 1.50018 9.31031 1.5H2.68881C2.49004 1.50004 2.29941 1.57899 2.15881 1.7195L1.56431 2.3145C1.40193 2.47737 1.27912 2.67535 1.20535 2.89319C1.13157 3.11102 1.10881 3.34289 1.13881 3.57091C1.16881 3.79893 1.25077 4.01702 1.37838 4.20836C1.50599 4.3997 1.67583 4.55918 1.87481 4.6745M1.87481 10.5V4.6745M3.37481 9H5.24981C5.34927 9 5.44465 8.96049 5.51498 8.89017C5.5853 8.81984 5.62481 8.72446 5.62481 8.625V6.75C5.62481 6.65054 5.5853 6.55516 5.51498 6.48484C5.44465 6.41451 5.34927 6.375 5.24981 6.375H3.37481C3.27536 6.375 3.17997 6.41451 3.10965 6.48484C3.03932 6.55516 2.99981 6.65054 2.99981 6.75V8.625C2.99981 8.832 3.16781 9 3.37481 9Z" stroke="white" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const NewWebIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
      <path d="M2.06 12H21.94" stroke="white" strokeWidth="1.5" />
      <path d="M12 2.06C14.5 4.5 15.5 8.25 15.5 12C15.5 15.75 14.5 19.5 12 21.94C9.5 19.5 8.5 15.75 8.5 12C8.5 8.25 9.5 4.5 12 2.06Z" stroke="white" strokeWidth="1.5" />
    </svg>
  );

  const BlueCalendarIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 2V6" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 2V6" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 10H21" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const BlueRulesIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mt-0.5">
      <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 2V8H20" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 13H8" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 17H8" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 9H8" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const getCategoryIcon = (name) => {
    if (!name) return <CategoryIcon className="w-3 h-3 mr-1 text-[#00ADEF]" />;
    const n = name.toLowerCase();
    if (n.includes("food") || n.includes("beverage") || n.includes("dining")) {
      return <FoodIcon className="w-3 h-3 mr-1 text-[#00ADEF]" />;
    }
    return <CategoryIcon className="w-3 h-3 mr-1 text-[#00ADEF]" />;
  };

  return (
    /* 1. Outer Shell (Straight, White) */
    <div className="flex flex-col items-center bg-white w-full max-w-[370px] min-h-[680px] border border-gray-100 shadow-xl relative pb-6 mx-auto overflow-hidden">

      {/* 2. Status Bar - Placed on the shell */}
      <div className="w-full h-[40px] flex justify-between items-center px-7 pt-3 mb-2">
        <span className="text-black font-semibold text-[14px] font-sans tracking-tight">9:41</span>
        <div className="flex items-center gap-1.5 pt-0.5">
          {/* iOS Signal Icon */}
          <svg width="17" height="11" viewBox="0 0 17 11" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0" y="7" width="3" height="4" rx="1" fill="black" />
            <rect x="4" y="5" width="3" height="6" rx="1" fill="black" />
            <rect x="8" y="3" width="3" height="8" rx="1" fill="black" />
            <rect x="12" y="0" width="3" height="11" rx="1" fill="black" />
          </svg>
          {/* Fixed Textured iOS Wifi Icon */}
          <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 13.5C10.1046 13.5 11 12.6046 11 11.5C11 10.3954 10.1046 9.5 9 9.5C7.89543 9.5 7 10.3954 7 11.5C7 12.6046 7.89543 13.5 9 13.5Z" fill="black" />
            <path d="M13.9189 8.24355C12.5452 7.2144 10.8407 6.64355 9 6.64355C7.1593 6.64355 5.45479 7.2144 4.08115 8.24355L3.30835 7.21045C4.94523 5.98273 6.94503 5.31015 9 5.31015C11.055 5.31015 13.0548 5.98273 14.6917 7.21045L13.9189 8.24355Z" fill="black" />
            <path d="M16.9189 5.24355C14.7176 3.59254 11.979 2.64355 9 2.64355C6.021 2.64355 3.2824 3.59254 1.08115 5.24355L0.30835 4.21045C2.75345 2.37666 5.7601 1.31015 9 1.31015C12.2399 1.31015 15.2466 2.37666 17.6917 4.21045L16.9189 5.24355Z" fill="black" fillOpacity="0.6" />
          </svg>
          {/* iOS Battery Icon */}
          <svg width="24" height="12" viewBox="0 0 24 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.5" y="0.5" width="20" height="11" rx="2.5" stroke="black" strokeOpacity="0.35" />
            <rect x="2" y="2.25" width="17" height="7.5" rx="1.5" fill="black" />
            <path d="M22 4C22 4 23.5 4 23.5 6C23.5 8 22 8 22 8" stroke="black" strokeOpacity="0.35" strokeLinecap="round" />
          </svg>
        </div>
      </div>

      {/* 3. Floating Inner Offer Card */}
      <div className="w-[90%] bg-white rounded-[24px] shadow-[0_4px_30px_rgba(0,0,0,0.12)] flex flex-col items-center overflow-hidden mb-4 border border-gray-50">

        {/* Ticket-shaped image container */}
        <div className="ticket-image-multi-cutout w-[95%] h-[135px] mb-8 mx-auto mt-3" style={{ background: '#000', position: 'relative' }}>
          {allImages.length > 1 ? (
            <Slider className="w-full h-full review-details-carousel" {...settings}>
              {allImages.map((url, idx) => {
                const resolved = getS3PublicUrl(url);
                if (!url) return <div key={idx} className="w-full h-full bg-black" />;

                return (
                  <div key={idx} className="w-full h-[135px] outline-none">
                    <img
                      src={resolved}
                      className="w-full h-full object-cover"
                      style={{ background: 'transparent', borderRadius: 0 }}
                      alt=""
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                );
              })}
            </Slider>
          ) : (
            (() => {
              const url = allImages?.[0] || offerData?.thumbnailImage;
              const resolved = getS3PublicUrl(url);
              if (!url) return <div className="w-full h-full bg-black" />;

              return (
                <img
                  src={resolved}
                  className="w-full h-full object-cover"
                  style={{ background: 'transparent', borderRadius: 0 }}
                  alt=""
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              );
            })()
          )}
        </div>

        {/* Card Content Details */}
        <div className="flex flex-col items-start justify-start w-full px-5 pt-0 pb-2">
          {/* 1. Title */}
          <p className="font-poppins font-[700] text-[16px] text-[#222] mb-2 leading-tight">
            {offerData?.title}
          </p>

          {/* 2. Primary Tags */}
          <div className="flex flex-wrap gap-2 mb-3">
            {offerTypeTag && (
              <span className="bg-[#00ADEF] text-white text-[11px] font-[400] rounded-[4px] px-2 py-1.5 flex items-center gap-1.5">
                {offerData?.offerType === 'ONLINE_OFFER' ? <NewWebIcon /> : <NewInStoreIcon />}
                <span className="relative top-[1px]">{offerTypeTag}</span>
              </span>
            )}
            {discountText && (
              <span className="bg-[#00ADEF] text-white text-[11px] font-[400] rounded-[4px] px-2 py-1.5 flex items-center gap-1.5">
                {offerData?.offerDiscountType !== "FIXED_DOLLAR" && <NewDiscountIcon />}
                <span className="relative top-[1px]">{discountText}</span>
              </span>
            )}
          </div>

          {/* 3. Description */}
          <p
            className="font-poppins font-[400] text-[12px] text-[#303030] mb-3 break-words w-full leading-relaxed"
            style={{ wordBreak: "break-word", whiteSpace: "pre-line" }}
          >
            {String(offerData?.description || "").replace(/\r\n/g, "\n").replace(/\n{2,}/g, "\n")}
          </p>

          {/* 4. Secondary Tags */}
          <div className="flex flex-wrap gap-2 mb-3">
            {offerData?.category?.title && (
              <span className="bg-[#E6F7FF] text-[#00ADEF] text-[11px] font-[500] rounded-[4px] px-2 py-1 flex items-center">
                {getCategoryIcon(offerData?.category?.title)}
                {offerData?.category?.title}
              </span>
            )}
            {Array.isArray(offerData?.subCategory) && offerData?.subCategory.map((s, idx) => (
              <span key={idx} className="bg-[#E6F7FF] text-[#00ADEF] text-[11px] font-[500] rounded-[4px] px-2 py-1 flex items-center">
                {s?.title}
              </span>
            ))}
          </div>

          {/* Validity */}
          <div className="flex items-center gap-1 mb-2">
            <BlueCalendarIcon />
            <span className="text-[#767676] text-[12px] font-[500]">
              {offerData?.endDate
                ? `Valid until ${dayjs(offerData.endDate).format("DD/MM/YYYY")}`
                : (() => {
                  const rule = offerData?.necessaryRule;
                  const freq = offerData?.redemptionFrequency;
                  if (rule === "OFFER_AVAILABLE_THROUGHOUT_YEAR") {
                    if (freq === "ONCE_IN_12_MONTHS") return "Redeem Once Every Year";
                    if (freq === "DAILY") return "Redeemable Daily";
                    return "Valid throughout the year";
                  }
                  if (rule === "REDEEM_ONCE") {
                    if (freq === "ONCE_IN_12_MONTHS") return "Redeem Once in 12 Months";
                    if (freq === "DAILY") return "Redeemable Daily";
                    return "Redeem Once";
                  }
                  if (rule === "CHOOSE_DATE_RANGE") return "Date Range Offer";
                  return "Ongoing";
                })()}
            </span>
          </div>

          {/* Rules & Regulations */}
          <div className="flex items-start gap-1 mb-1">
            <BlueRulesIcon />
            <span className="text-[#767676] text-[12px] font-[500]">Rules and regulations:</span>
          </div>
          <div className={`${offerData?.ruleAndRegulation?.length >= 3 ? 'rules-list-anim' : ''} ${showAllRules ? 'expanded' : ''} w-full`}>
            <ul className="list-disc pl-8 text-[#303030] text-[11px] mb-2 font-[400]">
              {offerData?.ruleAndRegulation?.length > 0 ? (
                (showAllRules
                  ? offerData?.ruleAndRegulation
                  : offerData?.ruleAndRegulation.slice(0, 2)
                ).map((rule, idx) => (
                  <li key={idx} className="mb-1">{rule?.title}</li>
                ))
              ) : (
                <>
                  <li className="mb-1">Valid only in NSW.</li>
                  <li className="mb-1">This offer may also be used when dining alone.</li>
                  <li className="mb-1">Valid for Dine-In.</li>
                </>
              )}
            </ul>
          </div>

          {/* View More */}
          {offerData?.ruleAndRegulation?.length >= 3 && (
            <div className="w-full flex justify-start">
              <button
                className="text-[#767676] text-[12px] font-[500] flex items-center gap-1 mb-4 bg-transparent border-none p-0 cursor-pointer hover:underline"
                onClick={() => setShowAllRules(!showAllRules)}
              >
                {showAllRules ? "View Less" : "View More"}
                <span className="text-[14px]">{showAllRules ? " " : " >"}</span>
              </button>
            </div>
          )}
        </div>

        {/* Redeem Button - At the bottom of the inner card */}
        <div className="w-full px-5 mb-4">
          <button className="w-full bg-[#00ADEF] text-white font-poppins font-[600] text-[15px] rounded-[10px] py-3.5 mt-0 shadow-sm hover:bg-[#009bd6] transition-colors focus:outline-none">Redeem</button>
        </div>
      </div>
    </div>
  );
};

export default OfferDetails;
